import { useCallback } from 'react'
import { Redo2 } from 'lucide-react'
import { toast } from 'sonner'
import type {
  MessageAttachmentDisplay,
  MessageIntent,
  UserMessageDisplay,
} from '@/types'
import { CopyButton } from '@/components/ui/copy-button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Message,
  MessageAttachment,
  MessageAttachments,
  MessageContent,
} from '@/components/ai/message'
import { toFileUIPart } from '@/lib/attachments'
import { cn } from '@/lib/utils'
import { MessageFooter } from './MessageFooter'

interface UserMessageProps {
  event: UserMessageDisplay
}

function AttachmentChip({
  attachment,
}: {
  attachment: MessageAttachmentDisplay
}) {
  const chip = <MessageAttachment data={toFileUIPart(attachment)} />

  // Only an image we already have bytes for can be enlarged. A PDF, or an image
  // too big for the backend to inline, stays a plain paperclip chip.
  const isPreviewable =
    attachment.mediaType.startsWith('image/') && Boolean(attachment.url)
  if (!isPreviewable) return chip

  return (
    <Dialog>
      {/* A button, not `asChild` on the chip: MessageAttachment doesn't forward
          a ref, and this also makes the thumbnail keyboard-reachable. */}
      <DialogTrigger asChild>
        <button
          aria-label={`Open ${attachment.filename}`}
          className="cursor-zoom-in rounded-lg focus-visible:ring-2 focus-visible:ring-david-solid/40 focus-visible:outline-none"
          type="button">
          {chip}
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-3xl">
        <DialogTitle className="sr-only">{attachment.filename}</DialogTitle>
        <img
          alt={attachment.filename}
          className="max-h-[80vh] w-full rounded-md object-contain"
          src={attachment.url}
        />
      </DialogContent>
    </Dialog>
  )
}

const INTENT_MARKERS: Record<
  MessageIntent,
  { label: string; tooltip: string; Icon?: typeof Redo2 }
> = {
  queued: {
    label: 'Queued',
    tooltip: 'Waited for David to finish the previous message',
  },
  steer: {
    label: 'Sent mid-task',
    tooltip: 'Sent while David was still working on the previous message',
    Icon: Redo2,
  },
}

export function UserMessageIntentMarker({
  intent,
  reveal = false,
}: {
  intent: MessageIntent
  /** Fades the marker in; only for a bubble appearing now, not on reload. */
  reveal?: boolean
}) {
  const { label, tooltip, Icon } = INTENT_MARKERS[intent]
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'ml-auto flex items-center gap-1 text-xs text-muted-foreground',
              reveal &&
                'transition-[opacity,translate,filter] duration-fast ease-smooth-out starting:translate-y-1 starting:opacity-0 starting:blur-[2px]'
            )}
            tabIndex={0}>
            {Icon && <Icon aria-hidden className="size-3" />}
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export function UserMessage({ event }: UserMessageProps) {
  const handleCopy = useCallback(() => toast.success('Copied to clipboard'), [])
  const handleCopyError = useCallback(() => toast.error('Failed to copy'), [])

  return (
    <Message from="user" className="max-w-xl">
      {event.intent && (
        <UserMessageIntentMarker
          intent={event.intent}
          reveal={event.optimistic}
        />
      )}
      {event.attachments && event.attachments.length > 0 && (
        <MessageAttachments>
          {event.attachments.map((attachment, index) => (
            <AttachmentChip
              attachment={attachment}
              key={`${attachment.filename}-${index}`}
            />
          ))}
        </MessageAttachments>
      )}
      <MessageContent className="group-[.is-user]:bg-black/7 group-[.is-user]:rounded-xl group-[.is-user]:text-primary-high-contrast group-[.is-user]:p-4">
        <p className="text-base leading-relaxed whitespace-pre-wrap">
          {event.content}
        </p>
        <MessageFooter timestamp={event.timestamp}>
          <CopyButton
            content={event.content}
            onCopy={handleCopy}
            onError={handleCopyError}
          />
        </MessageFooter>
      </MessageContent>
    </Message>
  )
}
